import type { ServiceResponseDto } from '../types/api/service';
import type {
  PracSearchRequestDto,
  PracSearchResponseDto,
} from '../types/api/pracSearch';
import type {
  AdminPracMigrateRequestDto,
  AdminPracMigrateResponseDto,
  AdminDataMigrateRequestDto,
  AdminDataMigrateResponseDto,
  AdminProfileMigrateRequestDto,
  AdminProfileMigrateResponseDto,
} from '../types/api/adminMigrate';
import { BaseServiceClient } from './BaseServiceClient';

/**
 * AdminMigrateService
 *
 * Adapter for the admin_migrate_* backend operations (sync service).
 */
export class AdminMigrateService extends BaseServiceClient {
  constructor(apiService: any) {
    super(apiService);
  }

  private async call<T>(requestType: string, request: unknown): Promise<T> {
    let response: ServiceResponseDto;
    try {
      response = await this.callService(requestType, request);
    } catch (err: any) {
      throw new Error(`${requestType} failed: ${err?.message ?? 'unknown error'}`);
    }

    if (!response.success) {
      throw new Error(`${requestType} error: ${response.message || response.errorDescription}`);
    }

    try {
      return JSON.parse(response.payload) as T;
    } catch {
      throw new Error(`${requestType}: failed to parse response payload`);
    }
  }

  /**
   * Search for candidate source accounts to migrate from. Same request/response DTOs as
   * provider_account_search.
   */
  searchSourceAccounts(request: PracSearchRequestDto): Promise<PracSearchResponseDto> {
    return this.call<PracSearchResponseDto>('admin_migrate_search', request);
  }

  /**
   * Migrate the practice info from the source to the destination account. The response reports
   * how many profiles/data records remain to be moved via admin_migrate_data.
   */
  migratePractice(request: AdminPracMigrateRequestDto): Promise<AdminPracMigrateResponseDto> {
    return this.call<AdminPracMigrateResponseDto>('admin_migrate_prac', request);
  }

  /** Move one block of data records. Called iteratively by the caller. */
  migrateDataBlock(request: AdminDataMigrateRequestDto): Promise<AdminDataMigrateResponseDto> {
    return this.call<AdminDataMigrateResponseDto>('admin_migrate_data', request);
  }

  /** Move one block of profiles. Called iteratively by the caller, independent of data blocks. */
  migrateProfileBlock(request: AdminProfileMigrateRequestDto): Promise<AdminProfileMigrateResponseDto> {
    return this.call<AdminProfileMigrateResponseDto>('admin_migrate_profiles', request);
  }
}
